import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter, Mail, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // TOGGLE: Footer Email Display - Shows/hides email address on hover for anti-spam protection
  // Current State: false - Email is hidden until hover
  // How to Toggle: Controlled by hover events (automatic)
  // Category: Automatic
  const [showFooterEmail, setShowFooterEmail] = useState(false);

  // Email obfuscation function
  const getObfuscatedEmail = () => {
    // Using character codes to make it harder for bots to parse
    const parts = [
      String.fromCharCode(104, 101, 108, 108, 111), // 'hello'
      String.fromCharCode(64), // '@'
      String.fromCharCode(104, 121, 112, 101, 114, 104, 117, 109, 97, 110, 101), // 'hyperhumane'
      String.fromCharCode(46), // '.'
      String.fromCharCode(99, 111, 109) // 'com'
    ];
    return parts.join('');
  };

  const handleFooterEmailClick = () => {
    const email = getObfuscatedEmail();
    window.location.href = `mailto:${email}`;
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/neilsinghmamak/',
      icon: Linkedin,
    },
    {
      name: 'Email',
      action: handleFooterEmailClick,
      icon: Mail,
      isEmail: true,
    },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Skills', path: '/skills' },
    { name: 'Projects', path: '/projects' },
    { name: 'Perspectives', path: '/perspectives' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-primary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center space-x-3"
            >
              <div>
                <div className="text-2xl font-bold bg-gradient-to-r from-primary-100 to-primary-400 bg-clip-text text-transparent">
                  HyperHumane
                </div>
                <div className="text-primary-200 text-sm">Strategic Leadership for the Long Now</div>
              </div>
            </motion.div>
            <p className="text-primary-200 max-w-md text-left">
              Strategic leadership portfolio showcasing executive capabilities, organizational transformation, and business impact. 
              Building sustainable competitive advantages for the long now.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                social.isEmail ? (
                  <motion.button
                    key={social.name}
                    onClick={social.action}
                    onMouseEnter={() => setShowFooterEmail(true)}
                    onMouseLeave={() => setShowFooterEmail(false)}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 bg-primary-800 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <social.icon size={20} />
                  </motion.button>
                ) : (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 bg-primary-800 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <social.icon size={20} />
                  </motion.a>
                )
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-primary-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Get In Touch</h3>
            <div className="space-y-2 text-primary-300">
              <p 
                onMouseEnter={() => setShowFooterEmail(true)}
                onMouseLeave={() => setShowFooterEmail(false)}
                className="cursor-pointer"
              >
                {showFooterEmail ? getObfuscatedEmail() : 'Hover to reveal email address'}
              </p>
              <p>Based in San Francisco, CA</p>
            </div>
            <motion.button
              onClick={handleFooterEmailClick}
              onMouseEnter={() => setShowFooterEmail(true)}
              onMouseLeave={() => setShowFooterEmail(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-2 bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors"
            >
              <span>Let's Talk</span>
              <ExternalLink size={16} />
            </motion.button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-center text-primary-200 text-sm">
            <p>San Francisco, CA · © 2025 Neil Singh Mamak. All rights reserved.</p>
          </div>
          <div className="flex space-x-6 text-sm text-primary-400">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 